interface SavingsResult {
  label: string;
  icon: string;
  monthlySavings: number;
  annualSavings: number;
  fiveYearSavings: number;
  tenYearSavings: number;
  systemCost: number;
  totalIncentives: number;
  netCost: number;
  roiYears: number;
  savingsPercent: number;
  color: string;
  badge: string;
}

interface ResultsComparisonProps {
  results: SavingsResult[];
  monthlyBill: number;
}

export default function ResultsComparison({
  results,
  monthlyBill,
}: ResultsComparisonProps) {
  if (results.length === 0) {
    return (
      <div className="bg-brand-cream rounded-2xl p-12 text-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-gray-200 mx-auto mb-4">
          <i className="ri-arrow-up-line text-gray-400 text-2xl"></i>
        </div>
        <h3 className="text-lg font-bold text-gray-700 mb-2">
          Select at least one upgrade package
        </h3>
        <p className="text-sm text-gray-500">
          Choose from the options above to see your personalized savings
          breakdown
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-green-100">
          <i className="ri-bar-chart-grouped-line text-green-600 text-xl"></i>
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900">
            Your Savings Comparison
          </h3>
          <p className="text-xs text-gray-500">
            Based on your ${monthlyBill}/mo utility bill
          </p>
        </div>
      </div>

      <div
        className={`grid gap-6 ${
          results.length === 1
            ? "grid-cols-1"
            : results.length === 2
            ? "grid-cols-1 lg:grid-cols-2"
            : "grid-cols-1 lg:grid-cols-3"
        }`}
      >
        {results.map((r, idx) => (
          <div
            key={idx}
            className="bg-white rounded-2xl border-2 overflow-hidden transition-all duration-300 hover:shadow-xl"
            style={{ borderColor: r.color + "40" }}
          >
            {/* Header */}
            <div
              className="p-5 text-white relative"
              style={{
                background: `linear-gradient(135deg, ${r.color}, ${r.color}dd)`,
              }}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-2xl">{r.icon}</span>
                <span className="text-xs font-bold bg-white/20 px-3 py-1 rounded-full whitespace-nowrap">
                  {r.badge}
                </span>
              </div>
              <h4 className="font-bold text-sm">{r.label}</h4>
              <div className="mt-3">
                <div className="text-3xl font-bold">
                  ${r.monthlySavings.toLocaleString()}
                </div>
                <div className="text-xs opacity-80">saved per month</div>
              </div>
            </div>

            {/* Savings Ring */}
            <div className="flex justify-center py-5">
              <div className="relative w-28 h-28">
                <div className="w-full h-full rounded-full border-[10px] border-gray-100 relative">
                  <div
                    className="absolute inset-[-10px] rounded-full transition-all duration-1000"
                    style={{
                      background: `conic-gradient(${r.color} ${r.savingsPercent *
                        3.6}deg, transparent ${r.savingsPercent *
                        3.6}deg)`,
                      mask:
                        "radial-gradient(farthest-side, transparent calc(100% - 10px), #fff calc(100% - 10px))",
                      WebkitMask:
                        "radial-gradient(farthest-side, transparent calc(100% - 10px), #fff calc(100% - 10px))",
                    }}
                  ></div>
                </div>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <div className="text-2xl font-bold" style={{ color: r.color }}>
                    {Math.round(r.savingsPercent)}%
                  </div>
                  <div className="text-[10px] text-gray-500">
                    bill reduction
                  </div>
                </div>
              </div>
            </div>

            {/* Details */}
            <div className="px-5 pb-5 space-y-3">
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-xs text-gray-500">
                  Annual Savings
                </span>
                <span className="text-sm font-bold text-green-600">
                  ${r.annualSavings.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-xs text-gray-500">5-Year Savings</span>
                <span className="text-sm font-bold text-green-600">
                  ${r.fiveYearSavings.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-gray-100">
                <span className="text-xs text-gray-500">10-Year Savings</span>
                <span className="text-sm font-bold text-green-600">
                  ${r.tenYearSavings.toLocaleString()}
                </span>
              </div>
              <div className="bg-gray-50 rounded-xl p-3 space-y-2 mt-3">
                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-500">System Cost</span>
                  <span className="text-xs font-semibold text-gray-700">
                    ${r.systemCost.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-xs text-green-600">
                    Tax Credits &amp; Rebates
                  </span>
                  <span className="text-xs font-semibold text-green-600">
                    -${r.totalIncentives.toLocaleString()}
                  </span>
                </div>
                <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                  <span className="text-xs font-bold text-gray-700">
                    Your Net Cost
                  </span>
                  <span className="text-sm font-bold text-brand-navy">
                    ${r.netCost.toLocaleString()}
                  </span>
                </div>
              </div>

              <div
                className="rounded-xl p-3 text-center"
                style={{
                  backgroundColor: `rgba(27, 58, 107, 0.05)`,
                }}
              >
                <div className="text-xs text-gray-500">Pays for itself in</div>
                <div className="text-xl font-bold text-brand-navy">
                  {r.roiYears.toFixed(1)} years
                </div>
                <div className="text-[10px] text-gray-400">
                  Then it&apos;s pure savings
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
